import { Box, Button, ButtonGroup, Card, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography, Icon, IconButton, Avatar, CircularProgress } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Description as DescriptionIcon } from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, { useEffect, useState, useRef } from 'react';
import AptaTable from 'examples/AptaTable';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import axios from 'axios';
import { URL } from 'constants/constant';
import Swal from 'sweetalert2';

// Main component for managing Downloads
const DownloadsManagement = () => {
    const [selectedDownload, setSelectedDownload] = useState({ title: "", file: "" });
    const [open, setOpen] = useState(false);
    const [downloads, setDownloads] = useState([]);
    const [formType, setFormType] = useState('add');
    const [loading, setLoading] = useState(false);

    const handleOpen = (type, item = { title: "", file: "" }) => {
        setFormType(type);
        setSelectedDownload(item);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleSave = async (data) => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("title", data.title);
            if (data.file instanceof File) {
                formData.append("file", data.file);
            }

            if (formType === 'add') {
                await axios.post(`${URL}/api/admin/quicklink`, formData);
                Swal.fire("Success!", "Download added successfully!", "success");
            } else {
                await axios.patch(`${URL}/api/admin/quicklink/${data._id}`, formData);
                Swal.fire("Success!", "Download updated successfully!", "success");
            }

            handleClose();
            getDownloads();
        } catch (error) {
            console.error("Error saving download:", error);
            Swal.fire("Error!", "Failed to save download.", "error");
            handleClose();
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will delete the download permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${URL}/api/admin/quicklink/${id}`);
                    Swal.fire("Deleted!", "The download has been deleted.", "success");
                    getDownloads();
                } catch (err) {
                    console.error("Delete error:", err);
                    Swal.fire("Error!", "Failed to delete download.", "error");
                }
            }
        });
    };

    const getDownloads = async () => {
        try {
            const response = await axios.get(`${URL}/api/admin/quicklink`);
            if (response.data) {
                setDownloads(response.data.data.reverse());
            }
        } catch (err) {
            console.error("Error fetching downloads:", err);
            Swal.fire("Error!", "Failed to fetch downloads.", "error");
        }
    };

    useEffect(() => {
        getDownloads();
    }, []);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <Box display={'flex'} mb={3} justifyContent={'end'}>
                <Button startIcon={<Icon>add</Icon>} style={{ color: 'white' }} onClick={() => handleOpen('add')} variant='contained' color='primary'>
                    Add Quick Links
                </Button>
            </Box>
            <AptaTable
                columns={['Title', 'File', 'Actions']}
                rows={downloads?.map((item) => ({
                    Title: <MDTypography variant="caption">{item?.title}</MDTypography>,
                    File: item?.file ? (
                        <a href={`${URL}/${item.file}`} target="_blank" rel="noopener noreferrer">
                            <IconButton color="primary">
                                <DescriptionIcon />
                            </IconButton>
                        </a>
                    ) : null,
                    Actions: (
                        <ButtonGroup size='small'>
                            <Button color='info' onClick={() => handleOpen('edit', item)} >
                                <Icon color='info' >edit</Icon>
                            </Button>
                            <Button color="error" onClick={() => handleDelete(item?._id)}>
                                <Icon color='error'>delete</Icon>
                            </Button>
                        </ButtonGroup>
                    )
                }))}
            />
            <InputFormModal
                open={open}
                onClose={handleClose}
                onSave={handleSave}
                details={selectedDownload}
                type={formType}
                loading={loading}
            />
        </DashboardLayout>
    );
};

export default DownloadsManagement;

const InputFormModal = ({ open, onClose, onSave, details, type, loading }) => {
    const [formData, setFormData] = useState({ title: "", file: "" });
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (details) {
            setFormData(details);
        }
    }, [details]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, file });
        }
    };

    const handleSave = () => {
        onSave(formData);
    };

    const isSaveDisabled = () => {
        return !formData.title || (!formData.file && type === 'add') || loading;
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
            <DialogTitle>{type === 'add' ? 'Add Links' : 'Edit Links'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
                    <TextField
                        label="Title"
                        name="title"
                        type="text"
                        value={formData.title || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        disabled={loading}
                    />
                    <Box
                        sx={{
                            mt: 2,
                            mb: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 1,
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            Upload PDF file only:
                        </Typography>

                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={loading}
                        >
                            Choose PDF
                            <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                        </Button>

                        {formData.file && typeof formData.file !== 'string' && (
                            <Typography variant="caption" color="textSecondary">
                                Selected file: {formData.file.name}
                            </Typography>
                        )}

                        {type === 'edit' && typeof formData.file === 'string' && (
                            <Typography variant="caption" color="textSecondary">
                                Current file:&nbsp;
                                <a
                                    href={`${URL}/${formData.file}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {formData.file.split('/').pop()}
                                </a>
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="primary" disabled={loading}>
                    Cancel
                </Button>
                {loading ? (
                    <Box sx={{ px: 2, py: 1 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={isSaveDisabled()}
                        style={{ color: 'white' }}
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

